import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { requestSuratJalanRevisionApi } from "../../api/suratJalanApi";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { SuratJalanDetail } from "../../types/suratJalan";
import { getErrorMessage } from "../../utils/errorHandling";
import { toastEmitter } from "../../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

interface SuratJalanRequestRevisionDialogProps {
  suratJalanId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (suratJalan: SuratJalanDetail) => void;
}

const SuratJalanRequestRevisionDialog: React.FC<SuratJalanRequestRevisionDialogProps> = ({
  suratJalanId,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setNote("");
  }, [open]);

  const handleSubmit = async () => {
    const trimmed = note.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const updated = await requestSuratJalanRevisionApi(suratJalanId, trimmed);
      toastEmitter.emit(t("suratJalan.revisionRequested" as TranslationKeys), "success");
      onSuccess(updated);
      onClose();
    } catch (err) {
      toastEmitter.emit(getErrorMessage(err, t, "requestSuratJalanRevision"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid={TestId.SURAT_JALAN.REVISION_DIALOG}
    >
      <DialogTitle>{t("orders.requestRevision" as TranslationKeys)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          margin="dense"
          label={t("orders.revisionNote" as TranslationKeys)}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          inputProps={{ "data-testid": TestId.SURAT_JALAN.REVISION_NOTE }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
        <Button
          variant="contained"
          disabled={submitting || note.trim().length === 0}
          onClick={handleSubmit}
          data-testid={TestId.SURAT_JALAN.REVISION_SUBMIT}
        >
          {t("orders.requestRevision" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuratJalanRequestRevisionDialog;
